import React, { useCallback, useEffect, useState } from 'react';
import styles from './map.module.scss';
import { MarkerF, InfoWindowF, GoogleMap, LoadScript } from '@react-google-maps/api';
import { IMapPoint } from '../../../models/map/IMapPoint';
import IImage from '../../../models/viewModels/base/IImage';
import ImageComponent from '../../atoms/image-component/image-component';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import { Breakpoint } from '../../../constants/breakpoints';
import Markdown from 'markdown-to-jsx';

export type MapProps = {
  points: IMapPoint[];
  fallbackImage: IImage;
  apiKey: string;
  zoomLevel?: number;
  id?: string;
};

export default function Map({ points, fallbackImage, apiKey, zoomLevel = 12, id }: MapProps) {
  const [mapError, setMapError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const mapOptions = {
    streetViewControl: false,
    mapTypeControl: false,
    styles: [
      {
        featureType: `poi`,
        stylers: [{ visibility: `off` }],
      },
    ],
  };

  const [isBreakpointUp, setIsBreakpointUp] = useState(!deviceDetect.isMobile);

  useWindowResize((newWidth) => setIsBreakpointUp(newWidth >= Breakpoint.Large), 100);

  useEffect(() => {
    (window as any).gm_authFailure = () => {
      setMapError(true);
    };
  }, []);

  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const handleActiveMarker = (markerTitle: string) => {
    setActiveMarker(markerTitle);
    setIsOpen(true);
  };

  const onLoad = useCallback(
    function callback(map) {
      if (points.length > 1) {
        var bounds = new google.maps.LatLngBounds();
        points.forEach((point) => {
          bounds.extend({ lat: Number(point.location.lat), lng: Number(point.location.lng) });
        });
        // resize based on markers, native
        map.fitBounds(bounds);
      }
    },
    [points],
  );

  return (
    <div id={id}>
      {!mapError ? (
        <div>
          <LoadScript googleMapsApiKey={apiKey} onError={() => setMapError(true)}>
            <GoogleMap
              mapContainerStyle={{
                width: `100%`,
                height: isBreakpointUp ? `480px` : `315px`,
              }}
              onLoad={onLoad}
              center={points[0].location}
              zoom={zoomLevel}
              options={mapOptions}
              onClick={() => setIsOpen(false)}
            >
              {points.map((item) => (
                <MarkerF
                  key={item.title}
                  title={item.title}
                  position={item.location}
                  icon={item.icon?.secureUrl}
                  onClick={() => handleActiveMarker(item.title)}
                >
                  {isOpen && activeMarker === item.title && (
                    <InfoWindowF
                      onCloseClick={() => {
                        setIsOpen(false);
                      }}
                    >
                      <div className={`${styles[`map-marker-markdown`]}`} key={item.title}>
                        <h3>{item.title}</h3>
                        {item.description ? <Markdown>{item.description}</Markdown> : <></>}
                      </div>
                    </InfoWindowF>
                  )}
                </MarkerF>
              ))}
            </GoogleMap>
          </LoadScript>
        </div>
      ) : (
        fallbackImage && (
          <div className={`${styles[`fallback-image-wrapper`]}`}>
            <ImageComponent src={fallbackImage.secureUrl} altText={fallbackImage.alt} />
          </div>
        )
      )}
    </div>
  );
}
